import { useState } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Typography } from "@mui/material";

import { IRules, IRulesElegibility, IElegibilityPlans, IElegibilityRules, CoverageTypes, CoverageGeographicTypes, RemissionPeriodTypes, ElegibleType, PriceVersion } from "../../Setup.types";
import { ButtonDelete, Container, Row, SelectForm, Subtitle, TextField, Title } from "./Rules.styled";
import { maskPercentage } from "./Rules.utils";
import { useQuery } from "@tanstack/react-query";
import api from "services/api";
import { MultiSelectWithSelectAll } from "v2/components/ui";

import { currentVersionOn, legacyPrefillCents, previewPlanContribution, setPlanContribution } from "./planPanel.api";
import { PlanPricePanel, centsToReais, ptDay, saoPauloToday, serverMessage, versionSummary } from "./PlanPricePanel";

interface IProps {
  rules: IRules;
  handleChange: (payload: Partial<IRules>) => void;
  /** Contrato já gravado. Sem ele (criação), preço e contribuição por plano ficam fechados. */
  workspaceId?: string;
  /** `planCode` dos planos gravados no servidor: só eles têm painel, que grava pelo `planCode`. */
  savedPlanCodes?: string[];
  /** A remoção de um plano com dado do painel foi confirmada: o próximo Salvar manda o flag ao backend. */
  onConfirmRemovePlanWithPrice?: () => void;
}

type ContributionChoice = { index: number; value: boolean | null; wouldClose: number | null };

/** Dado do painel no plano: o backend recusa sumir com ele sem a confirmação (spec 4.4). */
const hasPanelData = (p: IElegibilityPlans) => (p.activePrice?.length ?? 0) > 0 || !!p.inactivePrice || p.contributory !== undefined;

function priceSummary(versions: PriceVersion[] | undefined, today: string): string | null {
  if (!versions?.length) return null;
  const current = currentVersionOn(versions, today);
  return current ? versionSummary(current) : `A partir de ${ptDay(versions[0].validFrom)}: ${versionSummary(versions[0])}`;
}

/** Só versão gravada diz "Valor único". O legado aparece como legado, para a configuradora confirmar no painel. */
function activeSummary(plan: IElegibilityPlans, today: string): string {
  const summary = priceSummary(plan.activePrice, today);
  if (summary) return summary;
  const legacy = legacyPrefillCents(plan.planValue);
  return legacy === null ? "Não cadastrado" : `Valor antigo R$ ${centsToReais(legacy)}, confirme no painel`;
}

function inactiveSummary(plan: IElegibilityPlans, today: string): string {
  if (plan.inactivePrice?.mode === "SAME_AS_ACTIVE") return "Igual ao de ativos";
  return priceSummary(plan.inactivePrice?.versions, today) ?? "Não cadastrado";
}

const offersText = (n: number) => (n === 1 ? "1 oferta aberta seria fechada" : `${n} ofertas abertas seriam fechadas`);

// Larguras fixas e iguais no cabeçalho e nas linhas: a linha inteira cabe sem o flex encolher cada coluna de um jeito.
const PLAN_COLS = { plan: 200, code: 140, price: 180, contribution: 200, roles: 220 };
const PLAN_ROW_GAP = 16;

function Cell({ width, children }: { width: number; children: React.ReactNode }) {
  return <Box sx={{ width, flexShrink: 0, "& .MuiFormControl-root": { width: "100%", maxWidth: "none" } }}>{children}</Box>;
}

function PriceCell({ text, saved, hint, onEdit }: { text: string; saved: boolean; hint: string; onEdit: () => void }) {
  return (
    <Box>
      <Typography variant="body2">{text}</Typography>
      <Button size="small" onClick={onEdit} disabled={!saved} sx={{ p: 0, minWidth: 0 }}>
        editar
      </Button>
      {!saved && (
        <Typography variant="caption" color="text.secondary" display="block">
          {hint}
        </Typography>
      )}
    </Box>
  );
}

export function RulesTabElegibility({ rules, handleChange, workspaceId, savedPlanCodes = [], onConfirmRemovePlanWithPrice }: IProps) {
  const { plans, elegibilityRules } = rules.rulesElegibility;
  const today = saoPauloToday();

  const [panel, setPanel] = useState<{ index: number; side: "active" | "inactive" } | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<number | null>(null);
  const [pendingContribution, setPendingContribution] = useState<ContributionChoice | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);

  const { data, isFetching, isFetched } = useQuery({
    queryKey: ["organization-roles", rules.rulesRegister.buyerFederalTaxId],
    queryFn: () => api.get(`/v2//operation/organization-roles/${rules.rulesRegister.buyerFederalTaxId}`),
    select: (data) => data?.data?.data as string[],
  });

  if (!isFetched && isFetching) return <div>Loading...</div>;

  /** Opções possíveis da coluna de Cargo. */
  const roleOptions = data ?? [];
  /** Opções possíveis para coluna de Documentos. */
  const documentOptions = ["Termo de Ciencia Beneficiario", "Termo de Recisão Contrato de Trabalho (TRCT)", "União Estável", "RG/CPF", "Certidão Nascimento", "Certidão de Casamento", "Caderneta da Criança", "Carta Assinada", "Ficha de Registro", "Comprovante Vínculo Empregatício", "Comprovante Matrícula Faculdade"];

  /** Função responsável especificamente por receber os valores alterados e formatar corretamente para handleChange. */
  function handleChangeElegibility(payload: Partial<IRulesElegibility>) {
    const updatedElegibilityFields = { ...rules.rulesElegibility, ...payload };

    handleChange({ ...rules, rulesElegibility: updatedElegibilityFields });
  }

  /** Função responsável por adicionar campos à array de planos individualmente. */
  function handleChangePlans(payload: Partial<IElegibilityPlans>, index: number) {
    const updatedPayload = plans.map((p, i) => i === index ? { ...p, ...payload } : p);

    handleChange({ ...rules, rulesElegibility: { ...rules.rulesElegibility, plans: updatedPayload }});
  }

  /** Função responsável por adicionar novo campo vazio à array de planos. */
  function handleAddPlan() {
    const newPlan = { planName: undefined, planCode: undefined, planValue: undefined, allowedRoles: [] };

    return handleChange({ ...rules, rulesElegibility: { ...rules.rulesElegibility, plans: [...plans, newPlan] }});
  }

  /** Função responsável por remover campo da array de planos. */
  function removePlan(index: number) {
    const updatedPayload = plans.filter((_p, i) => i !== index);

    return handleChange({ ...rules, rulesElegibility: { ...rules.rulesElegibility, plans: updatedPayload }});
  }

  /** Plano com preço ou contribuição só sai da lista depois da confirmação. */
  function handleRemovePlan(index: number) {
    if (hasPanelData(plans[index])) return setPendingRemoval(index);
    removePlan(index);
  }

  function confirmRemoval() {
    if (pendingRemoval === null) return;
    onConfirmRemovePlanWithPrice?.();
    removePlan(pendingRemoval);
    setPendingRemoval(null);
  }

  // O painel grava pelo planCode: código repetido na tela gravaria no plano errado, então não abre.
  const isSaved = (plan: IElegibilityPlans) =>
    !!workspaceId && !!plan.planCode && savedPlanCodes.includes(plan.planCode) && plans.filter((p) => p.planCode === plan.planCode).length === 1;

  /** O plano gravado pelo painel substitui o do estado; nome e cargos ainda não salvos ficam como estão. */
  function handlePlanSaved(saved: IElegibilityPlans, index: number) {
    const updatedPayload = plans.map((p, i) => (i === index ? { ...saved, planName: p.planName, allowedRoles: p.allowedRoles } : p));

    handleChange({ ...rules, rulesElegibility: { ...rules.rulesElegibility, plans: updatedPayload } });
  }

  async function saveContribution(index: number, value: boolean | null) {
    try {
      handlePlanSaved(await setPlanContribution(workspaceId as string, plans[index].planCode as string, value), index);
    } catch (error) {
      setPlanError(serverMessage(error, "Não foi possível gravar a contribuição do plano."));
    }
  }

  /** Spec 6.1: antes de gravar, a prévia diz quantas ofertas abertas fechariam. Zero grava direto. */
  async function handleContribution(index: number, value: boolean | null) {
    setPlanError(null);
    let wouldClose: number | null;
    try {
      ({ wouldClose } = await previewPlanContribution(workspaceId as string, plans[index].planCode as string, value));
    } catch (error) {
      return setPlanError(serverMessage(error, "Não foi possível gravar a contribuição do plano."));
    }
    if (wouldClose === 0) return saveContribution(index, value);
    setPendingContribution({ index, value, wouldClose });
  }

  function confirmContribution() {
    if (!pendingContribution) return;
    setPendingContribution(null);
    saveContribution(pendingContribution.index, pendingContribution.value);
  }

  /** Função responsável por adicionar campos à array de regras de elegibilidade individualmente. */
  function handleChangeRules(payload: Partial<IElegibilityRules>, index: number) {
    const updatedPayload = elegibilityRules.map((r, i) => i === index ? { ...r, ...payload } : r);

    handleChange({ ...rules, rulesElegibility: { ...rules.rulesElegibility, elegibilityRules: updatedPayload }});
  }

  /** Função responsável por remover campo da array de regras de elegibilidade. */
  function handleRemoveRule(index: number) {
    const updatedPayload = elegibilityRules.filter((_r, i) => i !== index);

    return handleChange({ ...rules, rulesElegibility: { ...rules.rulesElegibility, elegibilityRules: updatedPayload }});
  }

  /** Função responsável por adicionar novo campo vazio à array de planos. */
  function handleAddRule() {
    const newRule = { elegible: undefined, rule: undefined };

    return handleChange({ ...rules, rulesElegibility: { ...rules.rulesElegibility, elegibilityRules: [...elegibilityRules, newRule] }});
  }

  return (
    <Container>
      <Title>Planos</Title>
      <Row>
        <TextField
          label="Co-participação"
          value={maskPercentage(rules.rulesElegibility.coParticipation)}
          onChange={(e) => handleChangeElegibility({ coParticipation: e.target.value })}
        />
        <SelectForm
          label="Tipo de cobertura"
          value={rules.rulesElegibility.coverageType}
          onChange={(e) => handleChangeElegibility({ coverageType: e.target.value as CoverageTypes })}
        >
          <MenuItem value={"AMBULATORIAL"}>Ambulatorial</MenuItem>
          <MenuItem value={"AMBULATORIAL_HOSPITALAR"}>Ambulatorial + Hospitalar</MenuItem>
          <MenuItem value={"AMBULATORIAL_HOSPITALAR_OBSTETRA"}>Ambulatorial + Hospitalar + Obstetra</MenuItem>
          <MenuItem value={"HOSPITALAR"}>Hospitalar</MenuItem>
        </SelectForm>
        <SelectForm
          label="Abrangência geográfica"
          value={rules.rulesElegibility.coverageGeographic}
          onChange={(e) => handleChangeElegibility({ coverageGeographic: e.target.value as CoverageGeographicTypes })}
        >
          <MenuItem value={"GRUPO_DE_MUNICIPIOS"}>Grupo de municípios</MenuItem>
          <MenuItem value={"NACIONAL"}>Nacional</MenuItem>
          <MenuItem value={"REGIONAL"}>Regional</MenuItem>
        </SelectForm>
      </Row>
      <Title>Elegibilidade</Title>
      <Row>
        <SelectForm
          label="Contribuição"
          value={rules.rulesElegibility.contribution === true ? "SIM" : rules.rulesElegibility.contribution === false ? "NAO" : undefined}
          onChange={(e) => handleChangeElegibility({ contribution: e.target.value === "SIM" ? true : false })}
        >
          <MenuItem value={"SIM"}>Sim</MenuItem>
          <MenuItem value={"NAO"}>Nao</MenuItem>
        </SelectForm>
        <TextField
          label="Contribuição titular"
          value={maskPercentage(rules.rulesElegibility.contributionEmployee)}
          onChange={(e) => handleChangeElegibility({ contributionEmployee: e.target.value })}
        />
        <TextField
          label="Contribuição dependente"
          value={maskPercentage(rules.rulesElegibility.contributionDependent)}
          onChange={(e) => handleChangeElegibility({ contributionDependent: e.target.value })}
        />
      </Row>
      <Row>
        <TextField
          label="Limite técnico"
          value={maskPercentage(rules.rulesElegibility.limit)}
          onChange={(e) => handleChangeElegibility({ limit: e.target.value })}
        />
        <SelectForm
          label="Período remissão"
          value={rules.rulesElegibility.remissionPeriod}
          onChange={(e) => handleChangeElegibility({ remissionPeriod: e.target.value as RemissionPeriodTypes })}
        >
          <MenuItem value={"12_MONTHS"}>12 Meses</MenuItem>
        </SelectForm>
        <TextField
          label="Valor taxa implantação"
          disabled
          value={rules.rulesElegibility.implantationCost}
          onChange={(e) => handleChangeElegibility({ implantationCost: e.target.value })}
        />
      </Row>
      <Title>Características do benefício</Title>
      <Row style={{ gap: PLAN_ROW_GAP }}>
        <Cell width={PLAN_COLS.plan}><Subtitle style={{ width: "auto" }}>Plano</Subtitle></Cell>
        <Cell width={PLAN_COLS.code}><Subtitle style={{ width: "auto" }}>Código</Subtitle></Cell>
        <Cell width={PLAN_COLS.price}><Subtitle style={{ width: "auto" }}>Valor ativos</Subtitle></Cell>
        <Cell width={PLAN_COLS.price}><Subtitle style={{ width: "auto" }}>Valor inativos</Subtitle></Cell>
        <Cell width={PLAN_COLS.contribution}><Subtitle style={{ width: "auto" }}>Contribuição do empregado</Subtitle></Cell>
        <Cell width={PLAN_COLS.roles}><Subtitle style={{ width: "auto" }}>Possíveis cargos</Subtitle></Cell>
      </Row>
      {plans.map((plan, index) => {
        const saved = isSaved(plan);
        const duplicated = !!plan.planCode && plans.filter((p) => p.planCode === plan.planCode).length > 1;
        const hint = !workspaceId
          ? "O preço se cadastra depois de criar o contrato, na edição."
          : duplicated
            ? "Código repetido: cada plano precisa de código próprio"
            : "Salve o plano antes";
        return (
          <Row key={index} style={{ alignItems: "center", gap: PLAN_ROW_GAP }}>
            <Cell width={PLAN_COLS.plan}>
              <TextField
                label="Plano"
                value={plan.planName}
                onChange={(e) => handleChangePlans({ planName: e.target.value }, index)}
              />
            </Cell>
            <Cell width={PLAN_COLS.code}>
              <TextField
                label="Código"
                value={plan.planCode}
                onChange={(e) => handleChangePlans({ planCode: e.target.value }, index)}
              />
            </Cell>
            <Cell width={PLAN_COLS.price}>
              <PriceCell text={activeSummary(plan, today)} saved={saved} hint={hint} onEdit={() => setPanel({ index, side: "active" })} />
            </Cell>
            <Cell width={PLAN_COLS.price}>
              <PriceCell text={inactiveSummary(plan, today)} saved={saved} hint={hint} onEdit={() => setPanel({ index, side: "inactive" })} />
            </Cell>
            <Cell width={PLAN_COLS.contribution}>
              <SelectForm
                label="Contribuição do empregado"
                labelId={`contribuicao-plano-${index}`}
                disabled={!saved}
                value={plan.contributory === true ? "SIM" : plan.contributory === false ? "NAO" : "CONTRATO"}
                onChange={(e) => handleContribution(index, e.target.value === "SIM" ? true : e.target.value === "NAO" ? false : null)}
              >
                <MenuItem value={"CONTRATO"}>Igual ao contrato</MenuItem>
                <MenuItem value={"SIM"}>Sim</MenuItem>
                <MenuItem value={"NAO"}>Não</MenuItem>
              </SelectForm>
            </Cell>
            <Cell width={PLAN_COLS.roles}>
              <MultiSelectWithSelectAll
                label="Possíveis cargos para o produto"
                value={plan.allowedRoles ?? []}
                options={roleOptions}
                onChange={(values) => handleChangePlans({ allowedRoles: values }, index)}
              />
            </Cell>
            <ButtonDelete color="error" data-testid={`remover-plano-${index}`} onClick={() => handleRemovePlan(index)} />
          </Row>
        );
      })}
      {planError && <Alert severity="error" onClose={() => setPlanError(null)}>{planError}</Alert>}
      <Button onClick={handleAddPlan}>+ Adicionar plano</Button>
      {panel && plans[panel.index] && workspaceId && (
        <PlanPricePanel
          open
          onClose={() => setPanel(null)}
          workspaceId={workspaceId}
          plan={plans[panel.index]}
          side={panel.side}
          invoicingType={rules.rulesInvoicing?.invoicingType}
          onSaved={(saved) => handlePlanSaved(saved, panel.index)}
        />
      )}
      <Dialog open={pendingContribution !== null} onClose={() => setPendingContribution(null)} aria-labelledby="contribuicao-plano-titulo">
        <DialogTitle id="contribuicao-plano-titulo">Mudar a contribuição do plano</DialogTitle>
        <DialogContent>
          <Typography>
            {pendingContribution?.wouldClose === null ? "Não foi possível contar as ofertas abertas" : offersText(pendingContribution?.wouldClose ?? 0)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingContribution(null)}>Cancelar</Button>
          <Button onClick={confirmContribution}>Confirmar</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={pendingRemoval !== null} onClose={() => setPendingRemoval(null)} aria-labelledby="remover-plano-titulo">
        <DialogTitle id="remover-plano-titulo">Remover o plano {pendingRemoval !== null ? plans[pendingRemoval]?.planName : ""}</DialogTitle>
        <DialogContent>
          <Typography>
            Este plano tem preço ou contribuição cadastrados no painel. Ao salvar, o plano sai do contrato junto com o histórico de preços.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemoval(null)}>Cancelar</Button>
          <Button color="error" onClick={confirmRemoval}>Remover</Button>
        </DialogActions>
      </Dialog>
      <Row>
        <Subtitle>Tipo</Subtitle>
        <Subtitle style={{ marginLeft: "-30px" }}>Regra (idade maxima)</Subtitle>
        <Subtitle>Regra (escolaridade máxima)</Subtitle>
        <Subtitle>Documentos</Subtitle>
      </Row>
      {elegibilityRules.map((rule, index) => (
        <Row style={{ alignItems: "center"}}>
          <SelectForm
            label="Elegíveis ao produto"
            value={rule.elegible}
            onChange={(e) => handleChangeRules({ elegible: e.target.value as ElegibleType }, index)}
          >
            <MenuItem value={"DEPENDENTS"}>Dependentes</MenuItem>
          </SelectForm>
          <TextField
            label="Regra"
            value={rule.rule}
            onChange={(e) => handleChangeRules({ rule: e.target.value }, index)}
          />
          <TextField
            label="Segunda regra"
            value={rule.secondRule}
            onChange={(e) => handleChangeRules({ secondRule: e.target.value }, index)}
          />
          <MultiSelectWithSelectAll
            label="Documentos"
            value={rule.documents ?? []}
            options={documentOptions}
            onChange={(values) => handleChangeRules({ documents: values }, index)}
          />
          <ButtonDelete color="error" onClick={() => handleRemoveRule(index)} />
        </Row>
      ))}
      <Button onClick={handleAddRule}>+ Adicionar</Button>
    </Container>
  );
}
